import { usePostHog } from '@posthog/react';
import useAuth from 'hooks/useAuth';
import { cloneElement } from 'react';
import { FormattedMessage } from 'react-intl';
import { errorAlert } from 'utils/alert';

// Types
import { TaskForm } from 'types/tasks';

// Utils
import { t } from 'utils/getTranslations';
import { useGoBack, useLeaveForm, useReturnToLocation } from 'utils/useNavigateWithReturnState';
import { useRejectedSubmitCapture } from 'utils/useRejectedSubmitCapture';

// Store/API
import { useSelector } from 'store';
import { useAddTaskMutation, useUpdateTaskMutation } from 'store/api/tasks';

// Components
import CancelButton from 'components/buttons/CancelButton';
import PermissionButton from 'components/buttons/PermissionButton';
import SaveButton from 'components/buttons/SaveButton';
import { useCreateDialogForm, useInCreateDialog } from 'components/create-dialog/CreateDialogContext';
import { useCreateDraft } from 'components/create-dialog/useCreateDraft';
import { DraftSaveMode } from 'components/drafts/DraftSaveMode';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import Section from 'components/section-menu/Section';
import { HighlightedFieldsProvider, SectionActionsProvider } from 'components/section-menu/SectionActionsContext';
import SectionLayout from 'components/section-menu/SectionLayout';
import SubSection from 'components/section-menu/SubSection';
import { useSectionErrors } from 'components/section-menu/useSectionErrors';
import useFocusedEditMode from 'hooks/useFocusedEditMode';
import { AssigneesFields } from './form-sections/AssigneesFields';
import { ConnectionsFields } from './form-sections/ConnectionsFields';
import { DeadlineCalendarFields, TaskEventFields } from './form-sections/DeadlineCalendarFields';
import { DmsFields } from './form-sections/DmsFields';
import { GeneralFields, OfficialFields } from './form-sections/GeneralFields';
import { NotesFields } from './form-sections/NotesFields';
import { eventValidationErrorId, TaskFormProps, useTaskForm } from './form-sections/useTaskForm';
import { taskFormToRequest } from './taskFormMapping';

const FIELD_TO_SECTION_MAP: Record<string, string> = {
    // General
    taskTypes: 'tasks.form.general',
    name: 'tasks.form.general',
    taskPriority: 'tasks.form.general',
    assignedToEmployees: 'tasks.form.general',
    assignedByEmployees: 'tasks.form.general',
    // Deadline & Calendar
    deadlineDate: 'tasks.form.deadline-calendar',
    'taskEvent.dateStart': 'tasks.form.deadline-calendar',
    'taskEvent.dateEnd': 'tasks.form.deadline-calendar',
    'taskEvent.timeStart': 'tasks.form.deadline-calendar',
    'taskEvent.timeEnd': 'tasks.form.deadline-calendar',
    'taskEvent.eventInvitees': 'tasks.form.deadline-calendar',
    'taskEvent.inviteSender': 'tasks.form.deadline-calendar',
};

const TaskPageForm = ({ type = 'add', initialData, classifiers }: TaskFormProps): JSX.Element => {
    useFocusedEditMode('tasks');
    const form = useTaskForm({ type, initialData, classifiers });
    const { methods, taskId, status, calendarSync } = form;
    useCreateDialogForm(methods);
    const handleSubmit = useRejectedSubmitCapture(methods.handleSubmit, type === 'edit' ? 'task-edit' : 'task');
    const draft = useCreateDraft({
        record: 'task',
        methods: { handleSubmit, formState: methods.formState },
        // Refused as a create is: on a wrong event date or time, or an event no organiser sends.
        toPayload: (formData: TaskForm) => {
            const eventErrorId = eventValidationErrorId(form);
            if (eventErrorId) {
                errorAlert(t(eventErrorId));
                return null;
            }
            try {
                return taskFormToRequest(formData);
            } catch {
                errorAlert(t('forms.error-submitting-the-form'));
                return null;
            }
        },
    });
    const inCreateDialog = useInCreateDialog();
    const returnTo = useReturnToLocation('/tasks');
    const goBack = useGoBack(returnTo);
    const leaveForm = useLeaveForm();

    const {
        formState: { errors },
    } = methods;
    const sectionErrors = useSectionErrors(errors, FIELD_TO_SECTION_MAP);
    const posthog = usePostHog();
    const { user } = useAuth();
    const [addTask] = useAddTaskMutation();
    const [updateTask] = useUpdateTaskMutation();

    const { buttonsDisabled } = useSelector((state) => state.buttons);

    const onSubmit = async (formData: TaskForm, action: 'create' | 'update' | 'save-done'): Promise<void> => {
        try {
            const eventErrorId = eventValidationErrorId(form);
            if (eventErrorId) {
                errorAlert(t(eventErrorId));
                return;
            }
            const data = taskFormToRequest(formData);

            if (action === 'save-done') {
                data.taskStatusTypeCode = 'DONE';
                action = 'update';
            }

            if (action === 'create') {
                // A draft is created by completing it: the task keeps the draft's id.
                const creation: Promise<{ id: number }> =
                    draft.draftId === null ? addTask({ ...data }).unwrap() : draft.complete(data);
                await creation
                    .then((created) => {
                        posthog.capture('task_created', {
                            type_codes: data.taskTypeCodes ?? [],
                            delegated: user
                                ? (data.assignedToEmployees ?? []).some(
                                      (assignee) => Number(assignee.id) !== Number(user.id)
                                  )
                                : false,
                        });
                        // The dialog stays on the page it was opened over, so it finishes with the new task instead.
                        leaveForm(inCreateDialog ? `/task/${created.id}` : returnTo);
                    })
                    .catch(console.error);
            } else if (action === 'update' && taskId && !isNaN(Number(taskId))) {
                await updateTask({
                    ...data,
                    id: taskId,
                })
                    .unwrap()
                    .then(() => {
                        leaveForm(returnTo);
                    })
                    .catch(console.error);
            }
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const submitCreateTask = () => {
        handleSubmit((data) => onSubmit(data, 'create'))();
    };

    const submitUpdateTask = () => {
        handleSubmit((data) => onSubmit(data, 'update'))();
    };

    const submitUpdateTaskAndMarkAsDone = () => {
        handleSubmit((data) => onSubmit(data, 'save-done'))();
    };

    const getFormHeader = () => {
        const actions: JSX.Element[] = [];

        if (type === 'add') {
            actions.push(
                <CancelButton onClick={goBack} />,
                ...(draft.button ? [draft.button] : []),
                <SaveButton permissions={['CREATE.TASKS']} onClick={submitCreateTask} disabled={buttonsDisabled} />
            );
        } else if (type === 'edit' && taskId) {
            actions.push(<CancelButton onClick={goBack} />);
            if (status !== 'DONE') {
                actions.push(
                    <PermissionButton
                        permissions={['UPDATE.TASKS.ALL', 'UPDATE.TASKS.MY_OWN', 'UPDATE.TASKS.CREATED_BY_ME']}
                        variant="outlined"
                        color="secondary"
                        onClick={submitUpdateTaskAndMarkAsDone}
                    >
                        <FormattedMessage id="tasks.form.save-mark-as-done" />
                    </PermissionButton>
                );
            }
            actions.push(
                <PermissionButton
                    permissions={['UPDATE.TASKS.ALL', 'UPDATE.TASKS.MY_OWN', 'UPDATE.TASKS.CREATED_BY_ME']}
                    color="secondary"
                    variant="contained"
                    size="small"
                    onClick={submitUpdateTask}
                    disabled={buttonsDisabled}
                >
                    <FormattedMessage id="save" />
                </PermissionButton>
            );
        }

        return (
            <ActionHeader
                title={type === 'edit' ? t('tasks.form.update-task') : t('tasks.form.add-new-task')}
                actions={actions.map((button, index) => cloneElement(button, { key: `action#${index}` }))}
                onBack={goBack}
                draft={draft.detail}
            />
        );
    };

    return (
        <SectionActionsProvider>
            <HighlightedFieldsProvider>
                <FormWrapper>
                    {getFormHeader()}
                    <DraftSaveMode mode={draft.mode}>
                        <SectionLayout sectionErrors={sectionErrors}>
                            <Section title="tasks.form.general" collapsible>
                                <GeneralFields form={form} />
                                <AssigneesFields form={form} />
                                <OfficialFields form={form} />
                                <SubSection title="tasks.form.task-connections">
                                    <ConnectionsFields form={form} />
                                </SubSection>
                            </Section>
                            <Section title="tasks.form.deadline-calendar" collapsible>
                                <DeadlineCalendarFields form={form} />
                                {calendarSync ? (
                                    <SubSection title="tasks.form.calendar-settings">
                                        <TaskEventFields form={form} />
                                    </SubSection>
                                ) : null}
                            </Section>

                            <Section title="tasks.form.dms-header" collapsible>
                                <DmsFields form={form} />
                            </Section>
                            <Section title="tasks.form.notes-header" collapsible>
                                <NotesFields form={form} />
                            </Section>
                        </SectionLayout>
                    </DraftSaveMode>
                </FormWrapper>
            </HighlightedFieldsProvider>
        </SectionActionsProvider>
    );
};

export default TaskPageForm;
